import { Stack } from '@sureco/design-system'
import { Button } from '@surecompanies/core_components'
import ContentSection from 'layouts/ContentSection/ContentSection'
import React, { useState } from 'react'
import { DownloadOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import {
  ContributionReportQueryKeys,
  useContributionReport,
} from 'modules/contribution-report-service/hooks/useContributionReport'
// import DownloadContributionButton from './DownloadContributionButton'
import ContributionReportGenerateModal from './ContributionReportGenerateModal'
import ContributionReportTable from './ContributionReportSectionTable'
import { ContributionReportJobsStatus } from 'types/contribution'
import {
  ContributionReportResponseManyDto,
  ContributionReportResponseOneDto,
} from 'modules/contribution-report-service/dtos'

interface ContributionReportSectionProps {
  clientPublicKey: string
  yearOptions: { value: number; label: string }[]
  selectedYear: number
}

export default function ContributionReportSection({
  clientPublicKey,
  yearOptions,
  selectedYear,
}: ContributionReportSectionProps) {
  /* --------------------------------- States --------------------------------- */
  const queryClient = useQueryClient()
  const [isGenerateReportDialogOpen, setIsGenerateReportDialogOpen] =
    useState(false)
  const [reportYear, setReportYear] = useState(selectedYear)

  /* --------------------------------- Queries -------------------------------- */
  const { generateContributionReport, getContributionReports } =
    useContributionReport()

  // Generate the contribution report
  const generateContributionReportMutation = generateContributionReport({
    mutationOptions: {
      onSuccess: (data) => {
        // Get existing reports from cache
        const queryKey = [ContributionReportQueryKeys.GET_ALL, clientPublicKey]
        const existingData =
          queryClient.getQueryData<ContributionReportResponseManyDto>(queryKey)

        // Get company name from existing reports or use a fallback
        const companyName =
          existingData?.companies?.[0]?.companyName ?? 'Unknown Company'

        // Create temporary report entries for each job ID
        const tempReports: ContributionReportResponseOneDto[] = data.jobIds.map(
          (jobId) => ({
            id: jobId,
            clientPublicKey,
            companyName,
            year: reportYear,
            status: ContributionReportJobsStatus.CREATED,
            progress: 0,
            createdAt: new Date(),
            updatedAt: new Date(),
            totalWorkerJobs: 0,
            completedWorkerJobs: 0,
            failedWorkerJobs: 0,
          })
        )

        // Update the cache directly with new temp reports
        queryClient.setQueryData<ContributionReportResponseManyDto>(
          queryKey,
          (oldData) => {
            const existingReports = oldData?.companies ?? []
            // Filter out any existing reports with the same IDs (avoid duplicates)
            const filteredReports = existingReports.filter(
              (report: ContributionReportResponseOneDto) =>
                !data.jobIds.includes(report.id)
            )
            const newData = {
              companies: [...tempReports, ...filteredReports],
            }
            return newData
          }
        )

        // Close the generate report dialog
        setIsGenerateReportDialogOpen(false)
      },
    },
  })

  // Query the contribution reports for the given client public key
  const getContributionReportsQuery = getContributionReports(clientPublicKey, {
    queryOptions: {
      queryKey: [ContributionReportQueryKeys.GET_ALL, clientPublicKey],
      enabled: !!clientPublicKey,
      gcTime: 30 * 60 * 1000,
      staleTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  })

  /* --------------------------------- Methods -------------------------------- */
  const handleGenerateContributionReport = () => {
    mutate({
      year: reportYear,
      companies: [{ clientPublicKey }],
    })
  }

  /* --------------------------------- Getters -------------------------------- */
  const { mutate } = generateContributionReportMutation
  const isGeneratingReport = generateContributionReportMutation.isPending
  const isLoadingReports =
    getContributionReportsQuery.isLoading ||
    getContributionReportsQuery.isFetching

  /* ---------------------------------- Doms ---------------------------------- */

  return (
    <>
      <ContentSection
        title="Contribution Reports"
        actions={
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            loading={isGeneratingReport || isLoadingReports}
            disabled={isGeneratingReport || isLoadingReports}
            onClick={() => setIsGenerateReportDialogOpen(true)}
          >
            Generate Contribution Report
          </Button>
        }
      >
        <Stack direction="row" space={20}>
          <ContributionReportTable
            reports={getContributionReportsQuery.data}
            isLoading={isLoadingReports}
          />
        </Stack>
      </ContentSection>
      <ContributionReportGenerateModal
        open={isGenerateReportDialogOpen}
        onCancel={() => setIsGenerateReportDialogOpen(false)}
        onConfirm={handleGenerateContributionReport}
        loading={isGeneratingReport || isLoadingReports}
        year={reportYear}
        yearOptions={yearOptions}
        onChangeYear={setReportYear}
      />
    </>
  )
}
